'use client'

import { Mail } from 'lucide-react'
import { NEWSLETTER_HONEYPOT_FIELD, useNewsletterSubscribe } from '@/lib/use-newsletter-subscribe'
import { cn } from '@/lib/utils'
import { TurnstileWidget } from './turnstile-widget'

interface NewsletterCtaInlineProps {
	/**
	 * Post slug. This CTA has no outbound `href` to attach `withUtm` params
	 * to (it POSTs to the same-origin `/api/newsletter`), so attribution is
	 * carried via the `source` field sent to the API instead.
	 */
	slug?: string
	/**
	 * Distinguishes the end-of-article callout ('article') from the one
	 * injected mid-article on long posts ('article-mid') in analytics.
	 */
	placement?: 'article' | 'article-mid'
}

/**
 * In-article newsletter callout, rendered directly under the article body.
 * Visually distinct from the footer form: a bordered accent-background
 * callout card, matching the visual language established by
 * `SafetyCheckCTA` (accent bar, icon badge, card shell) rather than the
 * plain footer form layout.
 */
export function NewsletterCtaInline({ slug, placement = 'article' }: NewsletterCtaInlineProps) {
	const source = slug ? `blog-article:${slug}` : 'blog-article'
	const { email, setEmail, honey, setHoney, status, errorMsg, turnstileRef, handleSubmit } =
		useNewsletterSubscribe({ placement, source })

	return (
		<aside
			className="my-10 rounded-xl border bg-card shadow-sm overflow-hidden"
			aria-label="Subscribe to the newsletter"
		>
			{/* Accent bar */}
			<div className="h-1 w-full bg-primary" />

			<div className="px-6 py-8 sm:px-10 sm:py-10">
				<div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
					{/* Icon */}
					<div
						className="flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary"
						aria-hidden="true"
					>
						<Mail size={28} strokeWidth={1.5} />
					</div>

					{/* Text + form */}
					<div className="flex-1 min-w-0 w-full">
						<h2 className="text-xl font-bold text-foreground mb-1">Enjoyed this article?</h2>

						{status === 'success' ? (
							<p className="text-sm text-green-600 dark:text-green-400">
								Check your inbox to confirm your subscription.
							</p>
						) : (
							<>
								<p className="text-muted-foreground text-sm leading-relaxed mb-4">
									Get the latest articles and extension insights delivered to your inbox.
								</p>
								<form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
									<input
										type="email"
										placeholder="you@example.com"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
										disabled={status === 'submitting'}
										className={cn(
											'flex-1 h-10 rounded-md border border-input bg-transparent px-3 py-1 text-sm placeholder:text-muted-foreground outline-none',
											'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
											'disabled:opacity-50 disabled:cursor-not-allowed',
										)}
									/>
									{/* Honeypot — visually + aria hidden. Real users never fill this. */}
									<div
										aria-hidden="true"
										style={{
											position: 'absolute',
											left: '-9999px',
											width: '1px',
											height: '1px',
											overflow: 'hidden',
											opacity: 0,
											pointerEvents: 'none',
										}}
									>
										<label>
											Website (leave empty)
											<input
												type="text"
												name={NEWSLETTER_HONEYPOT_FIELD}
												value={honey}
												onChange={(e) => setHoney(e.target.value)}
												tabIndex={-1}
												autoComplete="off"
											/>
										</label>
									</div>
									<TurnstileWidget ref={turnstileRef} action="newsletter" />
									<button
										type="submit"
										disabled={status === 'submitting'}
										className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none h-10 px-6 whitespace-nowrap"
									>
										{status === 'submitting' ? 'Subscribing...' : 'Subscribe'}
									</button>
								</form>
								{status === 'error' && (
									<p className="text-sm text-red-600 dark:text-red-400 mt-2">{errorMsg}</p>
								)}
								<p className="text-xs text-muted-foreground/70 mt-2">
									By subscribing, you agree to receive email updates. Unsubscribe anytime.
								</p>
							</>
						)}
					</div>
				</div>
			</div>
		</aside>
	)
}
